import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, Truck, ClipboardCheck, Users, Zap, Target, Sparkles, GraduationCap, Boxes, CalendarCheck, LifeBuoy, Wrench, Handshake, UserCog } from "lucide-react";
import { COMPANY, IMAGES, PRODUCTS, INDUSTRIES, DIFFERENCE, CATEGORIES } from "../lib/data";
import { Reveal } from "../components/site/Reveal";
import { SectionHead, CTABand } from "../components/site/Bits";
import { SectorSelector } from "../components/site/SectorSelector";

const DIFF_ICONS = {
  "Dedicated Account Management": UserCog,
  "Customized Recommendations": Sparkles,
  "Fleet Assessments": Truck,
  "Jobsite Evaluations": ClipboardCheck,
  "Product Training": GraduationCap,
  "Inventory Planning": Boxes,
  "Annual Reviews": CalendarCheck,
  "Rapid Response": Zap,
  "Emergency Support": LifeBuoy,
  "Technical Expertise": Wrench,
  "Long-Term Partnership": Handshake,
};

import { Seo } from "../components/site/Seo";

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo />
      {/* HERO */}
      <section className="relative pt-[72px]">
        <div className="relative min-h-[92vh] w-full overflow-hidden flex flex-col">
          <img src={IMAGES.hero} alt="Environmental risk management" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 grain opacity-[0.12]" />
          <div className="relative container-x flex-1 flex flex-col justify-center min-h-0 py-12 md:py-16">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }} className="max-w-3xl">
              <div className="kicker text-flame mb-6">{COMPANY.tagline}</div>
              <h1 className="font-display font-black text-white text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-balance">
                Best-in-class industrial products, matched to your sector.
              </h1>
              <p className="mt-7 text-white/80 text-lg max-w-2xl leading-relaxed">
                OneHarvest delivers proven, complimentary products across multiple categories — absorbents, water treatment, and ground protection — and matches them to how your sector actually works.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Link to="/solutions" data-testid="hero-solutions-cta" className="inline-flex items-center justify-center gap-2 bg-flame text-white px-7 py-4 font-semibold tracking-wide hover:bg-flame/90 transition-colors">
                  Explore Our Solutions <ArrowUpRight className="h-5 w-5" />
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section data-testid="philosophy-section" className="bg-background">
        <div className="container-x py-24 grid gap-12 lg:grid-cols-12 items-center">
          <Reveal className="lg:col-span-8">
            <div className="kicker text-flame mb-4">Focused Solutions. Better Results.</div>
            <p className="font-display font-bold text-2xl md:text-4xl text-ink leading-[1.15] tracking-tight text-balance">
              "{COMPANY.philosophy}"
            </p>
            <p className="mt-6 text-muted-foreground text-lg max-w-2xl leading-relaxed">
              Instead of a lengthy product catalog, we carefully select proven solutions that deliver measurable value. Our goal is to become your trusted partner — not another transactional vendor.
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-ink font-semibold hover:text-flame transition-colors">
              Our story <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section data-testid="home-industries" className="bg-background">
        <div className="container-x py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHead kicker="Explore by Sector" title="Solutions organized by sector" desc="Pick your sector to see how OneHarvest matches products to how your work actually runs — and watch each sector's portfolio grow over time." />
            <Link to="/industries" className="inline-flex items-center gap-2 text-ink font-semibold hover:text-flame transition-colors whitespace-nowrap">
              All sectors <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <SectorSelector />
        </div>
      </section>

      {/* PRODUCTS */}
      <section data-testid="home-products" className="bg-charcoal">
        <div className="container-x py-24">
          <SectionHead dark kicker="Featured Products" title="A curated portfolio of best-in-class solutions" desc="Each product earns its place. We represent only technologies we'd stake our name on — and we add complementary products to each category as we grow." />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link to={`/products/${p.slug}`} data-testid={`home-product-${p.slug}`} className="group relative block bg-ink border border-white/10 overflow-hidden hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal">
                  <span aria-hidden="true" className="absolute top-0 left-0 z-30 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
                  <div className={`relative aspect-[4/3] overflow-hidden ${p.fit === "contain" ? "bg-white" : "bg-black"}`}>
                    <img src={p.image} alt={p.name} className={`h-full w-full group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] ${p.fit === "contain" ? "object-contain p-4" : "object-cover"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6">
                    <div className="kicker text-flame">{p.category}</div>
                    <h3 className="mt-2 font-display font-bold text-2xl text-white group-hover:text-flame transition-colors">{p.cardTitle || p.name}</h3>
                    <p className="mt-3 text-sm text-white/60 leading-relaxed">{p.tagline}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-flame text-sm font-semibold">
                      View product <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LAND STEWARDSHIP TEASER */}
      <section className="relative overflow-hidden" data-testid="home-stewardship">
        <img src={IMAGES.farmlandWide} alt="Rolling farmland" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-ink/85" />
        <div className="absolute inset-0 grain opacity-10" />
        <div className="relative container-x py-24 grid gap-10 lg:grid-cols-12 items-center">
          <Reveal className="lg:col-span-7">
            <div className="kicker text-flame mb-4">Land Stewardship · Resource Preservation</div>
            <h2 className="font-display font-extrabold text-white text-3xl md:text-5xl tracking-tight leading-[1.08] text-balance">
              We don't just protect the project. We help preserve the land.
            </h2>
            <p className="mt-6 text-white/75 text-lg max-w-2xl leading-relaxed">
              The ground under today's drilling and construction becomes tomorrow's premium agriculture and livestock land. BoneDry, Pinkwater, and Ground Protection keep that resource clean, intact, and productive — protecting the landowner's asset and the operator's legacy.
            </p>
            <Link to="/land-stewardship" data-testid="home-stewardship-cta" className="mt-8 inline-flex items-center gap-2 bg-flame text-white px-7 py-4 font-semibold tracking-wide hover:bg-flame/90 transition-colors">
              Explore our stewardship approach <ArrowUpRight className="h-5 w-5" />
            </Link>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="grid gap-3">
              {[
                ["Protect the resource", "Keep soil, topsoil, and the water table clean and uncontaminated."],
                ["Preserve the value", "Well-stewarded land commands a premium as ag/livestock demand outpaces supply."],
                ["Lead with vision", "Operators turn environmental care into a real value-add and stronger relationships."],
              ].map(([t, d]) => (
                <div key={t} className="group border border-white/10 bg-white/[0.03] px-5 py-4 overflow-hidden hover:bg-flame hover:border-flame hover:-translate-y-1 hover:shadow-2xl transition-[transform,box-shadow,border-color,background-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)]">
                  <div className="font-display font-bold text-white text-base">{t}</div>
                  <div className="text-sm text-white/60 mt-1 leading-relaxed group-hover:text-white/90 transition-colors duration-300">{d}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* PRODUCT CATEGORIES */}
      <section data-testid="home-categories" className="bg-background relative overflow-hidden">
        <div className="container-x py-24">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <SectionHead kicker="Our Product Categories" title="Curated categories, built to grow" desc="We organize our portfolio into focused product categories — and add proven products to each over time as we expand across sectors." />
            <Link to="/solutions" className="inline-flex items-center gap-2 text-ink font-semibold hover:text-flame transition-colors whitespace-nowrap">
              Browse all categories <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c.slug} delay={(i % 4) * 0.08}>
                <Link to={c.to || "/solutions"} data-testid={`home-category-${c.slug}`} className="group relative block border border-border bg-card h-full overflow-hidden hover:border-flame/60 hover:-translate-y-1.5 hover:shadow-2xl transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.25,1,0.5,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-flame focus-visible:ring-offset-2">
                  <span aria-hidden="true" className="absolute top-0 left-0 z-30 h-full w-1 bg-flame origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500 ease-out" />
                  <div className={`relative aspect-[4/3] overflow-hidden ${c.fit === "contain" ? "bg-white" : "bg-charcoal"}`}>
                    <img src={c.image} alt={c.name} className={`h-full w-full group-hover:scale-110 transition-transform duration-700 ease-[cubic-bezier(0.25,1,0.5,1)] ${c.fit === "contain" ? "object-contain p-4" : "object-cover"}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display font-bold text-lg text-ink leading-snug">{c.name}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.tagline}</p>
                    <span className="mt-4 inline-flex items-center gap-2 text-flame text-sm font-semibold">
                      View products <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY DIFFERENT */}
      <section data-testid="home-difference" className="bg-muted/40 border-t border-border">
        <div className="container-x py-20">
          <SectionHead kicker="Why We're Different" title="Many companies sell products. We want to become your trusted partner." desc="Customers feel like they have their own dedicated Solutions Manager — because they do." />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-7">
            {DIFFERENCE.map((d, i) => {
              const Icon = DIFF_ICONS[d] || Sparkles;
              return (
                <Reveal key={d} delay={(i % 4) * 0.06}>
                  <div className="group flex items-start gap-3 border-t border-border pt-4">
                    <Icon className="h-5 w-5 flex-shrink-0 text-flame/80 mt-0.5 group-hover:text-flame transition-colors" strokeWidth={1.75} />
                    <span className="min-w-0 break-words text-ink text-sm font-medium leading-snug group-hover:text-flame transition-colors">{d}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
